import React from 'react';
import type { Team } from '@/types';
import { getTeamDisplayName } from '@/utils/team';

interface TeamCardProps {
  team: Team;
  className?: string;
  onClick?: (team: Team) => void;
}

const TeamCard: React.FC<TeamCardProps> = ({
  team,
  className = '',
  onClick,
}) => {
  const handleClick = () => {
    if (onClick) {
      onClick(team);
    }
  };

  // 获取核心选手（按ELO排序前5名）
  const getCorePlayersText = () => {
    if (!team.players || team.players.length === 0) {
      return team.playerCount
        ? `共 ${team.playerCount} 名选手`
        : '暂无选手信息';
    }

    const sortedPlayers = [...team.players]
      .sort((a, b) => b.currentElo - a.currentElo)
      .slice(0, 5);

    return sortedPlayers.map(player => player.playerId).join(', ');
  };

  return (
    <div
      className={`
        bg-white 
        border border-border 
        rounded-xl 
        p-6 
        shadow-card 
        hover:shadow-card-hover 
        hover:border-glory-gold 
        transition-all 
        duration-300 
        cursor-pointer
        ${className}
      `}
      onClick={handleClick}
      role='button'
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      {/* 战队名称和地区 */}
      <div className='mb-4'>
        <h3 className='text-text-primary font-medium text-base'>
          {getTeamDisplayName(team)}
          {team.region && (
            <span className='text-text-secondary ml-2'>· {team.region}</span>
          )}
        </h3>
      </div>

      {/* 平均ELO显示 */}
      <div className='mb-4 text-center'>
        <div
          className='text-glory-gold font-bold leading-tight'
          style={{
            fontSize: 'var(--font-size-elo)',
            fontFamily: 'var(--font-family-data)',
          }}
        >
          {team.averageElo
            ? `${Math.round(team.averageElo).toLocaleString()} 平均ELO`
            : '计算中'}
        </div>
      </div>

      {/* 排名和选手数信息 */}
      <div className='mb-4 space-y-2'>
        {team.teamRank && (
          <div className='flex justify-between items-center text-sm'>
            <span className='text-text-secondary'>排名:</span>
            <span
              className='text-glory-gold-dark font-semibold'
              style={{ fontSize: 'var(--font-size-rank)' }}
            >
              #{team.teamRank}
            </span>
          </div>
        )}

        <div className='flex justify-between items-center text-sm'>
          <span className='text-text-secondary'>选手数:</span>
          <span className='text-text-primary font-medium'>
            {team.players?.length || team.playerCount || 0}
          </span>
        </div>
      </div>

      {/* 核心选手信息 */}
      <div className='pt-3 border-t border-border'>
        <div className='text-xs text-text-secondary mb-2'>核心选手:</div>
        <div className='text-sm text-text-primary leading-relaxed'>
          {getCorePlayersText()}
        </div>
      </div>
    </div>
  );
};

export default TeamCard;
